"use strict";
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SelectButton = exports.Select = exports.SelectValue = void 0;
var styled_1 = __importDefault(require("@emotion/styled"));
var react_aria_components_1 = require("react-aria-components");
var theme_1 = require("../theme");
/**
 * A select value component that displays the currently selected option.
 * Extends the AriaSelectValue component with custom styling.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/Select.html}
 * @see {@link https://compass.circleci.com/?path=/story/rac-select--default}
 *
 * @example
 * ```tsx
 * <Select>
 *   <SelectValue />
 *   <Button>
 *     <ArrowDropDownIcon />
 *   </Button>
 *   <Popover>
 *     <ListBox>
 *       <ListBoxItem>Option 1</ListBoxItem>
 *       <ListBoxItem>Option 2</ListBoxItem>
 *     </ListBox>
 *   </Popover>
 * </Select>
 * ```
 */
exports.SelectValue = (0, styled_1.default)(react_aria_components_1.SelectValue)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  min-width: 0;\n  flex: 1;\n  text-align: left;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n"], ["\n  min-width: 0;\n  flex: 1;\n  text-align: left;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n"])));
/**
 * A select component that allows users to choose from a list of options.
 * Provides a dropdown interface with proper accessibility and styling.
 *
 * @example
 * ```tsx
 * <Select>
 *   <SelectButton>
 *     <SelectValue />
 *   </SelectButton>
 *   <Popover>
 *     <ListBox>
 *       <ListBoxItem>Option 1</ListBoxItem>
 *       <ListBoxItem>Option 2</ListBoxItem>
 *     </ListBox>
 *   </Popover>
 * </Select>
 * ```
 *
 * @param autoComplete - Describes the type of autocomplete functionality the input should provide if any
 * @param name - The name of the input, used when submitting an HTML form
 * @param isOpen - Sets the open state of the menu
 * @param defaultOpen - Sets the default open state of the menu
 * @param disabledKeys - The item keys that are disabled. These items cannot be selected, focused, or otherwise interacted with
 * @param isDisabled - Whether the input is disabled
 * @param isRequired - Whether user input is required on the input before form submission
 * @param isInvalid - Whether the input value is invalid
 * @param validate - A function that returns an error message if a given value is invalid
 * @param placeholder - Temporary text that occupies the text input when it is empty
 * @param selectedKey - The currently selected key in the collection (controlled)
 * @param defaultSelectedKey - The initial selected key in the collection (uncontrolled)
 * @param autoFocus - Whether the element should receive focus on render
 * @param validationBehavior - Whether to use native HTML form validation or ARIA attributes
 * @param children - The children of the component. Can be ReactNode or a render function
 * @param className - The CSS className for the element. Can be string or a function
 * @param style - The CSS styles for the element. Can be CSSProperties or a function
 * @param onOpenChange - Method that is called when the open state of the menu changes
 * @param onSelectionChange - Handler that is called when the selection changes
 * @param onFocus - Handler that is called when the element receives focus
 * @param onBlur - Handler that is called when the element loses focus
 * @param onFocusChange - Handler that is called when the element's focus status changes
 * @param onKeyDown - Handler that is called when a key is pressed
 * @param onKeyUp - Handler that is called when a key is released
 * @param id - The element's unique identifier
 * @param excludeFromTabOrder - Whether to exclude the element from the sequential tab order
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.Select = (0, styled_1.default)(react_aria_components_1.Select)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n\n  &[data-invalid] > button {\n    border-color: ", ";\n  }\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n\n  &[data-invalid] > button {\n    border-color: ", ";\n  }\n"])), theme_1.theme.space.space8, theme_1.theme.colors.red500);
/**
 * A styled button component for the select input.
 * Provides visual feedback for different states (hover, focus, disabled).
 */
exports.SelectButton = (0, styled_1.default)(react_aria_components_1.Button)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row;\n  gap: ", ";\n  background: ", ";\n  justify-content: space-between;\n  align-items: center;\n  box-sizing: border-box;\n  height: 40px;\n  border: ", " solid ", ";\n  padding: ", ";\n  min-width: 200px;\n  max-width: 560px;\n  border-radius: ", ";\n  outline: none;\n  color: ", ";\n\n  &[data-disabled] {\n    opacity: ", ";\n    cursor: not-allowed;\n  }\n\n  &[data-hovered] {\n    border-color: ", ";\n  }\n\n  &[data-focus-visible] {\n    outline: ", " solid ", ";\n    outline-offset: ", ";\n  }\n"], ["\n  display: flex;\n  flex-direction: row;\n  gap: ", ";\n  background: ", ";\n  justify-content: space-between;\n  align-items: center;\n  box-sizing: border-box;\n  height: 40px;\n  border: ", " solid ", ";\n  padding: ", ";\n  min-width: 200px;\n  max-width: 560px;\n  border-radius: ", ";\n  outline: none;\n  color: ", ";\n\n  &[data-disabled] {\n    opacity: ", ";\n    cursor: not-allowed;\n  }\n\n  &[data-hovered] {\n    border-color: ", ";\n  }\n\n  &[data-focus-visible] {\n    outline: ", " solid ", ";\n    outline-offset: ", ";\n  }\n"])), theme_1.theme.space.space4, theme_1.theme.colors.surface200, theme_1.theme.borderWidths.regular, theme_1.theme.colors.n80, theme_1.theme.space.space12, theme_1.theme.radii.field, theme_1.theme.colors.n900, theme_1.theme.opacity.semiOpaque, theme_1.theme.colors.n200, theme_1.theme.borderWidths.thick, theme_1.theme.colors.blue400, theme_1.theme.space.space2);
var templateObject_1, templateObject_2, templateObject_3;
//# sourceMappingURL=Select.js.map